import { useState, useMemo } from 'react'
import type { SmartNotesNodeData } from '../types'

interface UseSmartNotesReferencesProps {
  id: string
  data: SmartNotesNodeData
  syncNodeData: (updates: Partial<SmartNotesNodeData>) => void
}

export const useSmartNotesReferences = ({
  id,
  data,
  syncNodeData,
}: UseSmartNotesReferencesProps) => {
  const [isReferenceDialogVisible, setIsReferenceDialogVisible] = useState(false)

  // Function to compute available nodes dynamically from flow
  const availableNodes = useMemo(() => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    return allNodes
      .filter((n: any) => n.id !== id && n.data)
      .map((n: any) => {
        const dataPoints: {
          id: string
          label: string
          description?: string
        }[] = []

        // Add full document if exists
        if (n.data?.dataSettings?.documentSections?.fullDocument) {
          dataPoints.push({ id: 'fullDocument', label: 'Full Document' })
        }

        // Add all sections if exist
        const sections = n.data?.dataSettings?.documentSections?.sections || {}
        Object.entries(sections).forEach(([title]: [string, any]) => {
          dataPoints.push({ id: `section:${title}`, label: title })
        })

        // Add all exposed data points if exist
        const exposedData = n.data?.dataSettings?.exposedData || {}
        Object.entries(exposedData).forEach(([key, field]: [string, any]) => {
          if (field?.enabled) {
            dataPoints.push({
              id: `exposed:${key}`,
              label: field.label || key,
              description: field.description,
            })
          }
        })

        // Add content for specific node types
        if (n.type === 'nodesmartnotes' && n.data.content) {
          dataPoints.push({ id: 'content', label: 'Content' })
        }

        return {
          id: n.id,
          type: n.type,
          label: n.data.label || `${n.type} (${n.id})`,
          dataPoints,
        }
      })
      .filter((node) => node.dataPoints.length > 0)
  }, [id])

  // Handle saving references and refreshing context
  const handleSaveReferences = async (
    newReferences: Record<string, string[]>,
    refreshContext = false
  ) => {
    syncNodeData({ setReferences: newReferences })

    if (refreshContext) {
      await refreshLinkedKnowledge(newReferences)
    }

    setIsReferenceDialogVisible(false)
  }

  // Refresh linked knowledge based on references
  const refreshLinkedKnowledge = async (references: Record<string, string[]>) => {
    try {
      const allNodes = (window as any).__flowGetAllNodes?.() || []
      let compiledKnowledge = ''

      Object.entries(references).forEach(([nodeId, dataPointIds]) => {
        const refNode = allNodes.find((n: any) => n.id === nodeId)
        if (!refNode) return

        compiledKnowledge += `\n\n--- ${refNode.data?.label || refNode.type} (${nodeId}) ---\n`

        dataPointIds.forEach((dpId) => {
          if (dpId === 'fullDocument') {
            const fullDoc = refNode.data?.dataSettings?.documentSections?.fullDocument
            if (fullDoc) {
              compiledKnowledge += `Full Document:\n${fullDoc}\n\n`
            }
          } else if (dpId.startsWith('section:')) {
            const sectionTitle = dpId.replace('section:', '')
            const section = refNode.data?.dataSettings?.documentSections?.sections?.[sectionTitle]
            if (section) {
              compiledKnowledge += `${sectionTitle}:\n${section.content || section}\n\n`
            }
          } else if (dpId.startsWith('exposed:')) {
            const exposedKey = dpId.replace('exposed:', '')
            const exposedData = refNode.data?.dataSettings?.exposedData?.[exposedKey]
            if (exposedData?.enabled && exposedData.value) {
              compiledKnowledge += `${exposedData.label || exposedKey}:\n${exposedData.value}\n\n`
            }
          } else if (dpId === 'content' && refNode.data?.content) {
            compiledKnowledge += `Content:\n${refNode.data.content}\n\n`
          }
        })
      })

      // Update linked knowledge
      if (compiledKnowledge.trim()) {
        syncNodeData({
          linkedKnowledge: compiledKnowledge.trim(),
          context: compiledKnowledge.trim(),
        })
      }

      return compiledKnowledge.trim()
    } catch (error) {
      console.error('Error refreshing linked knowledge:', error)
      return ''
    }
  }

  // Handle removing a reference
  const handleRemoveReference = (nodeId: string) => {
    const newReferences = { ...data.setReferences }
    delete newReferences[nodeId]
    handleSaveReferences(newReferences, true)
  }

  // Handle updating a specific reference's data points
  const handleUpdateReference = (nodeId: string, dataPointIds: string[]) => {
    const newReferences = {
      ...data.setReferences,
      [nodeId]: dataPointIds,
    }
    handleSaveReferences(newReferences, true)
  }

  return {
    isReferenceDialogVisible,
    setIsReferenceDialogVisible,
    availableNodes,
    handleSaveReferences,
    handleRemoveReference,
    handleUpdateReference,
    refreshLinkedKnowledge,
  }
}
